import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  CheckCircle2,
  Coins,
  Lightbulb,
  Plus,
  Shield,
  Sparkles,
  Star,
  Target,
  Trash2,
  X,
} from "lucide-react";
import { formatFixtureDate, formatFixtureTime } from "../../../utils/formatFixtureDate";
import { isFixtureLive } from "../../../utils/p2pCreateHelpers";
import { APP_TOKEN_KEY, selectAppUser } from "../../../redux/appAuthSlice";
import { isUserVerified } from "../../../utils/profileHelpers";
import { syncBondChatForUser } from "../../../redux/bondchat/generalChatSlice";
import { CREATE_TIP_COST_COIN } from "../../../config/fees";
import {
  marketGroups,
  keysForGroup,
  keyLabel,
  fullOutcomeLabel,
} from "../../../data/predictionMarkets";

const PHASES = [
  { id: "picks", label: "Picks" },
  { id: "advice", label: "Advice" },
  { id: "publish", label: "Publish" },
];

const MAX_OUTCOMES = 5;
const MAX_ANALYSIS = 100;
const MAX_ADVICE = 500;

const emptyOutcome = () => ({ groupId: "oneXTwo:fullTime", key: "", confidence: "", analysis: "" });

const marketFromPick = (pick) => {
  if (!pick?.groupId || !pick?.key) return null;
  const [section, subSection] = pick.groupId.split(":");
  return { section, subSection, key: pick.key };
};

const resetForm = () => ({
  step: 1,
  outcomes: [emptyOutcome()],
  bettingAdvice: "",
  isPremium: false,
  price: "",
  error: "",
});

const parseConfidence = (value) => {
  const c = parseInt(String(value).replace(/[^0-9]/g, ""), 10);
  if (Number.isNaN(c)) return null;
  return Math.min(100, Math.max(0, c));
};

const validateOutcomes = (outcomes) => {
  if (!outcomes.length) return "Add at least one outcome.";
  const selections = new Set();
  for (let i = 0; i < outcomes.length; i += 1) {
    const o = outcomes[i];
    const market = marketFromPick(o);
    const c = parseConfidence(o.confidence);
    const analysis = o.analysis.trim();
    if (!market) return `Pick ${i + 1}: select an outcome from the market.`;
    const dupKey = `${market.section}|${market.subSection}|${market.key}`;
    if (selections.has(dupKey)) return "Each outcome must be unique.";
    selections.add(dupKey);
    if (c == null) return `Pick ${i + 1}: confidence must be 0–100.`;
    if (!analysis) return `Pick ${i + 1}: add a short analysis.`;
    if (analysis.length > MAX_ANALYSIS) return `Pick ${i + 1}: analysis max ${MAX_ANALYSIS} characters.`;
  }
  return "";
};

const validateAdvice = (bettingAdvice) => {
  const text = bettingAdvice.trim();
  if (!text) return "Betting advice is required.";
  if (text.length > MAX_ADVICE) return `Betting advice max ${MAX_ADVICE} characters.`;
  return "";
};

const validatePremium = (isPremium, price) => {
  if (!isPremium) return "";
  const p = parseFloat(String(price).replace(/[^0-9.]/g, ""));
  if (Number.isNaN(p) || p <= 0) return "Premium tips need a unlock price above 0 COIN.";
  return "";
};

const confidenceTone = (value) => {
  const c = parseConfidence(value) ?? 0;
  if (c >= 75) return "high";
  if (c <= 35) return "low";
  return "mid";
};

const PickCard = ({ index, item, canRemove, onChange, onRemove, teamNames }) => {
  const conf = parseConfidence(item.confidence) ?? 0;
  const tone = confidenceTone(item.confidence);
  const analysisLeft = MAX_ANALYSIS - item.analysis.length;
  const [section, subSection] = (item.groupId || "").split(":");
  const keys = keysForGroup(section, subSection);

  return (
    <article className="prdc-pick">
      <header className="prdc-pick-head">
        <span className="prdc-pick-badge">
          <Target size={11} aria-hidden />
          Pick {index + 1}
        </span>
        {canRemove ? (
          <button
            type="button"
            className="prdc-pick-remove"
            onClick={onRemove}
            aria-label={`Remove pick ${index + 1}`}
          >
            <Trash2 size={14} />
          </button>
        ) : null}
      </header>

      <div className="prdc-field">
        <label className="prdc-field-label" htmlFor={`prdc-market-${index}`}>
          Market
        </label>
        <select
          id={`prdc-market-${index}`}
          className="prdc-input prdc-select"
          value={item.groupId}
          onChange={(e) => onChange(index, "groupId", e.target.value)}
        >
          {marketGroups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.label}
            </option>
          ))}
        </select>
      </div>

      <div className="prdc-field">
        <span className="prdc-field-label">Outcome</span>
        <div className="prdc-market-chips" role="radiogroup" aria-label="Outcome">
          {keys.map((key) => (
            <button
              key={key}
              type="button"
              className={`prdc-market-chip${item.key === key ? " prdc-market-chip-on" : ""}`}
              onClick={() => onChange(index, "key", key)}
            >
              {keyLabel(section, key, teamNames)}
            </button>
          ))}
        </div>
      </div>

      <div className="prdc-field">
        <label className="prdc-field-label" htmlFor={`prdc-conf-${index}`}>
          Confidence
        </label>
        <div className="prdc-conf-row">
          <input
            id={`prdc-conf-${index}`}
            type="number"
            className="prdc-input prdc-input-conf"
            placeholder="0–100"
            min={0}
            max={100}
            inputMode="numeric"
            value={item.confidence}
            onChange={(e) =>
              onChange(index, "confidence", e.target.value.replace(/[^0-9]/g, "").slice(0, 3))
            }
          />
          <span className="prdc-conf-suffix">%</span>
        </div>
        <div className="prdc-conf-track" aria-hidden>
          <span
            className={`prdc-conf-fill prdc-conf-fill-${tone}`}
            style={{ width: `${conf}%` }}
          />
        </div>
      </div>

      <div className="prdc-field">
        <label className="prdc-field-label" htmlFor={`prdc-analysis-${index}`}>
          Analysis
        </label>
        <textarea
          id={`prdc-analysis-${index}`}
          className="prdc-textarea prdc-textarea-sm"
          placeholder="Why this outcome? (max 100 chars)"
          maxLength={MAX_ANALYSIS}
          rows={2}
          value={item.analysis}
          onChange={(e) => onChange(index, "analysis", e.target.value)}
        />
        <span className={`prdc-char-count${analysisLeft < 15 ? " prdc-char-count-warn" : ""}`}>
          {analysisLeft} left
        </span>
      </div>
    </article>
  );
};

const CreatePredictionModal = ({ open, fixture, league, onClose, onSubmit }) => {
  const dispatch = useDispatch();
  const user = useSelector(selectAppUser);
  const coinBalance = Number(user?.COIN) || 0;
  const canAffordTip = coinBalance >= CREATE_TIP_COST_COIN;
  const [step, setStep] = useState(1);
  const [outcomes, setOutcomes] = useState([emptyOutcome()]);
  const [bettingAdvice, setBettingAdvice] = useState("");
  const [isPremium, setIsPremium] = useState(false);
  const [price, setPrice] = useState("");
  const [error, setError] = useState("");

  const live = fixture ? isFixtureLive(fixture) : false;
  const fixtureId = fixture?._id || fixture?.id;
  const teamNames = useMemo(
    () => ({ homeName: fixture?.homeTeam?.name, awayName: fixture?.awayTeam?.name }),
    [fixture?.homeTeam?.name, fixture?.awayTeam?.name]
  );

  const sortedPicks = useMemo(
    () =>
      [...outcomes]
        .map((o, i) => ({ ...o, index: i, market: marketFromPick(o) }))
        .filter((o) => o.market)
        .sort(
          (a, b) => (parseConfidence(b.confidence) ?? 0) - (parseConfidence(a.confidence) ?? 0)
        ),
    [outcomes]
  );

  const leadPick = sortedPicks[0] || null;

  const handleClose = useCallback(() => {
    const next = resetForm();
    setStep(next.step);
    setOutcomes(next.outcomes);
    setBettingAdvice(next.bettingAdvice);
    setIsPremium(next.isPremium);
    setPrice(next.price);
    setError(next.error);
    onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") handleClose();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, handleClose]);

  useEffect(() => {
    if (!open) return;
    const next = resetForm();
    setStep(next.step);
    setOutcomes(next.outcomes);
    setBettingAdvice(next.bettingAdvice);
    setIsPremium(next.isPremium);
    setPrice(next.price);
    setError(next.error);
    if (user?._id) dispatch(syncBondChatForUser(user._id));
  }, [open, fixtureId, dispatch, user?._id]);

  const updateOutcome = (idx, field, value) => {
    setOutcomes((prev) =>
      prev.map((o, i) => {
        if (i !== idx) return o;
        // Switching market group invalidates the previously selected outcome.
        if (field === "groupId") return { ...o, groupId: value, key: "" };
        return { ...o, [field]: value };
      })
    );
  };

  const addOutcome = () => {
    if (outcomes.length < MAX_OUTCOMES) setOutcomes((prev) => [...prev, emptyOutcome()]);
  };

  const removeOutcome = (idx) => {
    if (outcomes.length > 1) setOutcomes((prev) => prev.filter((_, i) => i !== idx));
  };

  const buildPayload = () => ({
    token: localStorage.getItem(APP_TOKEN_KEY),
    fixtureId,
    outcomes: outcomes.map((o) => ({
      market: marketFromPick(o),
      confidence: parseConfidence(o.confidence) ?? 0,
      analysis: o.analysis.trim(),
    })),
    bettingAdvice: bettingAdvice.trim(),
    isPremium: Boolean(isPremium),
    price: isPremium ? Math.max(0, parseFloat(String(price).replace(/[^0-9.]/g, "")) || 0) : 0,
  });

  const submitCreate = () => {
    const token = localStorage.getItem(APP_TOKEN_KEY);

    if (!token) {
      setError("Session expired. Please sign in again.");
      return;
    }
    if (!fixtureId) {
      setError("Missing match data. Close and pick a fixture again.");
      return;
    }
    if (live) {
      setError("This match has already started. Pick an upcoming fixture.");
      return;
    }
    if (!isUserVerified(user)) {
      setError("Verify your account before publishing predictions.");
      return;
    }
    if (!canAffordTip) {
      setError(
        `You need ${CREATE_TIP_COST_COIN} COIN to publish a tip. You have ${Math.floor(coinBalance)}.`
      );
      return;
    }

    const premiumErr = validatePremium(isPremium, price);
    if (premiumErr) {
      setError(premiumErr);
      return;
    }

    const payload = buildPayload();
    const submit = onSubmit;
    handleClose();
    submit?.(payload);
  };

  const goNext = () => {
    setError("");
    if (live) {
      setError("This match has already started.");
      return;
    }
    if (step === 1) {
      const err = validateOutcomes(outcomes);
      if (err) {
        setError(err);
        return;
      }
      setStep(2);
      return;
    }
    if (step === 2) {
      const err = validateAdvice(bettingAdvice);
      if (err) {
        setError(err);
        return;
      }
      setStep(3);
      return;
    }

    const err = validatePremium(isPremium, price);
    if (err) {
      setError(err);
      return;
    }
    submitCreate();
  };

  const goBack = () => {
    setError("");
    if (step === 1) {
      handleClose();
      return;
    }
    setStep((s) => s - 1);
  };

  if (!open || !fixture) return null;

  const isLastStep = step === 3;
  const primaryLabel = isLastStep ? "Publish tip" : "Continue";
  const stepTitle =
    step === 1 ? "Your picks" : step === 2 ? "Betting advice" : "Review & publish";
  const adviceLeft = MAX_ADVICE - bettingAdvice.length;

  return createPortal(
    <div className="pcm2-root prdc-modal-root" role="dialog" aria-modal="true" aria-labelledby="prdc-title">
      <button
        type="button"
        className="pcm2-backdrop"
        aria-label="Close"
        onClick={handleClose}
      />

      <div className="pcm2-sheet prdc-modal-sheet">
        <header className="pcm2-hero prdc-modal-hero">
          <div className="pcm2-hero-top">
            <div className="pcm2-hero-copy">
              <p className="pcm2-hero-eyebrow">
                <Sparkles size={12} aria-hidden />
                Community tip
              </p>
              <h2 id="prdc-title" className="pcm2-hero-title">
                {stepTitle}
              </h2>
            </div>
            <button
              type="button"
              className="pcm2-close"
              onClick={handleClose}
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>

          <div className="pcm2-fixture prdc-fixture-solid">
            <div className="pcm2-fixture-team">
              <div className="pcm2-fixture-crest">
                {fixture.homeTeam?.logo ? (
                  <img src={fixture.homeTeam.logo} alt="" />
                ) : (
                  <Shield size={20} />
                )}
              </div>
              <span>{fixture.homeTeam?.name || "Home"}</span>
            </div>
            <div className="pcm2-fixture-mid">
              <span className={`pcm2-fixture-live${live ? " pcm2-fixture-live-on" : ""}`}>
                {live ? "LIVE" : formatFixtureTime(fixture.date)}
              </span>
              {league?.name ? <span className="pcm2-fixture-league">{league.name}</span> : null}
            </div>
            <div className="pcm2-fixture-team pcm2-fixture-team-away">
              <div className="pcm2-fixture-crest">
                {fixture.awayTeam?.logo ? (
                  <img src={fixture.awayTeam.logo} alt="" />
                ) : (
                  <Shield size={20} />
                )}
              </div>
              <span>{fixture.awayTeam?.name || "Away"}</span>
            </div>
          </div>
          <p className="pcm2-fixture-date">
            {live ? "In progress — cannot publish" : formatFixtureDate(fixture.date)}
          </p>
        </header>

        <div className="prdc-progress-rail" aria-hidden>
          {PHASES.map((p, i) => (
            <span
              key={p.id}
              className={`prdc-progress-seg${i + 1 <= step ? " prdc-progress-seg-on" : ""}`}
            />
          ))}
        </div>

        <nav className="pcm2-progress prdc-modal-progress" aria-label="Create progress">
          {PHASES.map((p, i) => {
            const done = i + 1 < step;
            const current = i + 1 === step;
            return (
              <div
                key={p.id}
                className={`pcm2-progress-item${done ? " pcm2-progress-done" : ""}${
                  current ? " pcm2-progress-current" : ""
                }`}
              >
                <span className="pcm2-progress-dot">
                  {done ? <CheckCircle2 size={12} /> : i + 1}
                </span>
                <span className="pcm2-progress-label">{p.label}</span>
              </div>
            );
          })}
        </nav>

        <div className="pcm2-body prdc-modal-body">
          {error ? (
            <p className="pcm2-error prdc-modal-error" role="alert">
              {error}
            </p>
          ) : null}

          {step === 1 ? (
            <div className="prdc-step">
              <p className="prdc-step-lead">
                Add up to {MAX_OUTCOMES} outcomes. Pick each from a verified market — after full
                time we grade your picks and build your public track record.
              </p>
              <div className="prdc-picks">
                {outcomes.map((item, idx) => (
                  <PickCard
                    key={idx}
                    index={idx}
                    item={item}
                    canRemove={outcomes.length > 1}
                    onChange={updateOutcome}
                    onRemove={() => removeOutcome(idx)}
                    teamNames={teamNames}
                  />
                ))}
              </div>
              {outcomes.length < MAX_OUTCOMES ? (
                <button type="button" className="prdc-add-pick" onClick={addOutcome}>
                  <Plus size={14} />
                  Add pick ({outcomes.length}/{MAX_OUTCOMES})
                </button>
              ) : null}
            </div>
          ) : null}

          {step === 2 ? (
            <div className="prdc-step">
              <div className="prdc-panel prdc-panel-advice">
                <header className="prdc-panel-head">
                  <Lightbulb size={15} />
                  <span>Betting advice</span>
                </header>
                <p className="prdc-panel-copy">
                  Summarize how you would bet on this match. This appears at the bottom of your tip
                  card.
                </p>
                <textarea
                  id="prdc-advice"
                  className="prdc-textarea"
                  placeholder="Share your reasoning, stake approach, and key factors…"
                  maxLength={MAX_ADVICE}
                  rows={5}
                  value={bettingAdvice}
                  onChange={(e) => setBettingAdvice(e.target.value)}
                />
                <span className={`prdc-char-count${adviceLeft < 40 ? " prdc-char-count-warn" : ""}`}>
                  {adviceLeft} characters left
                </span>
              </div>
            </div>
          ) : null}

          {step === 3 ? (
            <div className="prdc-step">
              <section className="prdc-review">
                <header className="prdc-review-head">
                  <CheckCircle2 size={14} />
                  <span>Tip summary</span>
                </header>
                <ul className="prdc-review-picks">
                  {sortedPicks.map((pick) => (
                    <li key={pick.index}>
                      <strong>{fullOutcomeLabel(pick.market, teamNames)}</strong>
                      <span>{parseConfidence(pick.confidence) ?? 0}%</span>
                    </li>
                  ))}
                </ul>
                {bettingAdvice.trim() ? (
                  <p className="prdc-review-advice">{bettingAdvice.trim()}</p>
                ) : null}
              </section>

              <div className="prdc-tier-grid">
                <button
                  type="button"
                  className={`prdc-tier${!isPremium ? " prdc-tier-on" : ""}`}
                  onClick={() => {
                    setIsPremium(false);
                    setPrice("");
                  }}
                >
                  <Shield size={18} />
                  <span>
                    <strong>Free tip</strong>
                    <em>Everyone can read your full analysis</em>
                  </span>
                </button>
                <button
                  type="button"
                  className={`prdc-tier${isPremium ? " prdc-tier-on" : ""}`}
                  onClick={() => setIsPremium(true)}
                >
                  <Star size={18} />
                  <span>
                    <strong>Premium tip</strong>
                    <em>Readers unlock with COIN</em>
                  </span>
                </button>
              </div>

              {isPremium ? (
                <div className="prdc-panel prdc-panel-price">
                  <label className="prdc-field-label" htmlFor="prdc-price">
                    <Coins size={13} />
                    Unlock price
                  </label>
                  <div className="prdc-price-row">
                    <input
                      id="prdc-price"
                      type="number"
                      className="prdc-input"
                      min={1}
                      step={1}
                      inputMode="numeric"
                      placeholder="e.g. 50"
                      value={price}
                      onChange={(e) => setPrice(e.target.value.replace(/[^0-9]/g, ""))}
                    />
                    <span className="prdc-price-unit">COIN</span>
                  </div>
                  <p className="prdc-panel-note">You always see your own premium tips for free.</p>
                </div>
              ) : null}

              <p className="prdc-footnote">
                Posting as <strong>{user?.name || "you"}</strong> · {CREATE_TIP_COST_COIN} COIN to
                publish · up to 5 tips per UTC day
              </p>
              <div className={`prdc-cost${canAffordTip ? "" : " prdc-cost-short"}`}>
                <Coins size={14} aria-hidden />
                <span>
                  Publish cost <strong>{CREATE_TIP_COST_COIN} COIN</strong>
                  {" · "}
                  wallet {Math.floor(coinBalance)} COIN
                  {canAffordTip ? null : " — not enough Coin"}
                </span>
              </div>
            </div>
          ) : null}
        </div>

        <footer className="pcm2-foot prdc-modal-foot">
          {leadPick ? (
            <div className="pcm2-foot-pick prdc-foot-pick">
              <CheckCircle2 size={14} aria-hidden />
              <span>
                Top pick: {fullOutcomeLabel(leadPick.market, teamNames)}
                {leadPick.confidence !== "" ? ` · ${parseConfidence(leadPick.confidence) ?? 0}%` : ""}
              </span>
            </div>
          ) : (
            <p className="pcm2-foot-hint">
              {step === 1
                ? "Fill in at least one complete pick"
                : step === 2
                ? "Advice is required before publishing"
                : "Review your tip, then publish"}
            </p>
          )}
          <div className="pcm2-foot-actions">
            <button
              type="button"
              className="pcm2-btn pcm2-btn-ghost"
              onClick={goBack}
            >
              {step === 1 ? "Cancel" : "Back"}
            </button>
            <button
              type="button"
              className="pcm2-btn pcm2-btn-primary prdc-btn-publish"
              onClick={goNext}
              disabled={live || (isLastStep && !canAffordTip)}
            >
              {primaryLabel}
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default CreatePredictionModal;
